import React, { useState, useEffect } from 'react';
import { Bot, Save, AlertCircle } from 'lucide-react';
import firmService from '../../services/firmService';

const AI_MODELS = [
  { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4 (Recommended)' },
  { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku (Fast / Low Cost)' },
  { value: 'claude-opus-4-20250514', label: 'Claude Opus 4 (Most Capable)' },
];

const AISettings = () => {
  const [firms, setFirms] = useState([]);
  const [selectedFirmId, setSelectedFirmId] = useState('');
  const [aiModel, setAiModel] = useState('');
  const [monthlyTokenLimit, setMonthlyTokenLimit] = useState('');
  const [tokensUsed, setTokensUsed] = useState(0);
  const [currentMonth, setCurrentMonth] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    loadFirms();
  }, []);

  useEffect(() => {
    if (selectedFirmId) {
      loadSettings(selectedFirmId);
    }
  }, [selectedFirmId]);

  const loadFirms = async () => {
    try {
      const data = await firmService.getAllFirms();
      setFirms(data);
      if (data.length > 0) {
        setSelectedFirmId(data[0].id);
      }
    } catch (err) {
      console.error('Failed to load firms:', err);
      setError('Failed to load firms');
    }
  };

  const loadSettings = async (firmId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await firmService.getAISettings(firmId);
      setAiModel(data.ai_model || '');
      setMonthlyTokenLimit(data.monthly_token_limit != null ? String(data.monthly_token_limit) : '');
      setTokensUsed(data.tokens_used || 0);
      setCurrentMonth(data.current_month || '');
    } catch (err) {
      console.error('Failed to load AI settings:', err);
      setError('Failed to load AI settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await firmService.updateAISettings(selectedFirmId, {
        ai_model: aiModel || null,
        monthly_token_limit: monthlyTokenLimit ? parseInt(monthlyTokenLimit, 10) : null,
      });
      setAiModel(data.ai_model || '');
      setMonthlyTokenLimit(data.monthly_token_limit != null ? String(data.monthly_token_limit) : '');
      setTokensUsed(data.tokens_used || 0);
      setSuccess('AI settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Failed to save AI settings:', err);
      setError('Failed to save AI settings');
    } finally {
      setSaving(false);
    }
  };

  const formatMonth = (ym) => {
    if (!ym) return '';
    const [year, month] = ym.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const limit = monthlyTokenLimit ? parseInt(monthlyTokenLimit, 10) : null;
  const usagePercent = limit ? Math.min((tokensUsed / limit) * 100, 100) : 0;

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center">
          <Bot className="h-5 w-5 text-gray-500 mr-2" />
          <h2 className="text-lg font-medium text-gray-900">AI Settings</h2>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Configure AI model and monthly token limits per firm
        </p>
      </div>

      <div className="p-6">
        {/* Firm Picker */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Select Firm</label>
          <select
            value={selectedFirmId}
            onChange={(e) => setSelectedFirmId(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
          >
            {firms.map((firm) => (
              <option key={firm.id} value={firm.id}>{firm.name}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
            <span className="ml-3 text-gray-600">Loading...</span>
          </div>
        ) : (
          <>
            {/* Usage Display */}
            {currentMonth && (
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Usage for {formatMonth(currentMonth)}
                </h3>
                <div className="mb-2">
                  {limit ? (
                    <>
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>{tokensUsed.toLocaleString()} / {limit.toLocaleString()} tokens used</span>
                        <span>{usagePercent.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full transition-all ${
                            usagePercent >= 90 ? 'bg-red-500' :
                            usagePercent >= 70 ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${usagePercent}%` }}
                        ></div>
                      </div>
                      {usagePercent >= 100 && (
                        <div className="flex items-center mt-2 text-sm text-red-600">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          Token limit reached. AI features are disabled for this firm until next month.
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-gray-600">
                      {tokensUsed.toLocaleString()} tokens used (no limit)
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Settings Form */}
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">AI Model</label>
                <select
                  value={aiModel}
                  onChange={(e) => setAiModel(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                >
                  <option value="">System Default (Sonnet 4)</option>
                  {AI_MODELS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Applied to all AI features: chat, document processing, and document Q&A
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Monthly Token Limit
                </label>
                <input
                  type="number"
                  value={monthlyTokenLimit}
                  onChange={(e) => setMonthlyTokenLimit(e.target.value)}
                  placeholder="Unlimited"
                  min="0"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Leave blank for unlimited. When the limit is reached, AI features are disabled until next month.
                </p>
              </div>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  {error}
                </div>
              )}

              {success && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                  {success}
                </div>
              )}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default AISettings;
